/**
 * Crypt Pawn Component
 *
 * SVG character pawn used by the crypt skin. Depicts a skeletal/ghost figure
 * with a silver/grey head and a colored glowing body sphere.
 *
 * Color-specific gradients (body sphere) are parameterized via CryptPawnColorScheme.
 * Neutral elements (silver head, dark inner area, face features) are shared across
 * all color variants.
 *
 * ViewBox: 0 0 323 473 (matches celeste pawn for consistent road section scaling)
 */

import React from 'react';
import type { PawnProps } from '../../constants/types';
import type { CryptPawnColorScheme } from './constants/pawnColorSchemes';

interface CryptPawnInternalProps extends PawnProps {
  colorScheme: CryptPawnColorScheme;
}

const CryptPawnComponent = React.memo(function CryptPawnComponent({
  viewBox = '0 0 323 473',
  className = '',
  colorScheme
}: CryptPawnInternalProps) {
  const prefix = `${colorScheme.name}CryptPawn`;

  return (
    <svg
      viewBox={viewBox}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${colorScheme.name} crypt pawn`}
    >
      {/* Outer shell shadow */}
      <path
        d="M157.85 424.96C151.01 424.96 144.09 424.37 137.28 423.21C107.58 418.14 81.4699 402.65 63.7699 379.6C46.0199 356.48 38.8399 328.52 43.5599 300.87C45.9699 286.78 51.2999 273.63 59.4099 261.78C65.2199 241.96 76.5099 224.55 92.1599 211.25C91.3099 209.92 90.4799 208.55 89.6599 207.11C81.3399 192.55 76.6399 176.05 76.0799 159.39C75.7799 151.3 76.4699 143.08 78.1299 134.98C81.3899 119.05 88.1099 104.54 98.0999 91.83C107.74 79.54 120.3 69.42 134.41 62.58C148.41 55.77 164.01 52.17 179.52 52.17C186.48 52.17 193.5 52.88 200.38 54.29C207.14 55.68 213.65 57.69 219.73 60.27C225.48 62.68 231.04 65.63 236.23 69.02C241.26 72.3 246.11 76.13 250.66 80.4C259.2 88.45 266.26 97.82 271.65 108.26C277.09 118.79 280.66 130.07 282.25 141.78L282.31 142.21V142.23C282.53 143.85 282.71 145.54 282.86 147.35C283.55 155.93 283.16 164.6 281.71 173.12C281.52 174.25 281.3 175.38 281.07 176.53C277.9 192.06 271.42 206.27 261.83 218.75C259.44 221.88 256.82 224.92 254.04 227.77C251.53 230.35 248.89 232.81 246.16 235.09C256.38 251.46 261.86 270.22 262.06 289.56C267.61 305.44 269.07 322.47 266.27 338.86C257.76 388.74 212.16 424.94 157.85 424.94V424.96Z"
        fill={`url(#${prefix}_shadow)`}
      />

      {/* Body back gradient circle */}
      <path
        d="M263.343 338.401C272.81 282.959 231.947 229.726 172.073 219.503C112.2 209.279 55.988 245.936 46.5211 301.378C37.0541 356.821 77.9168 410.053 137.791 420.277C197.664 430.501 253.876 393.844 263.343 338.401Z"
        fill={`url(#${prefix}_bodyBack)`}
      />

      {/* Body dark inner ring (opacity 0.57) */}
      <path
        opacity="0.57"
        d="M255.962 317.795C265.429 262.353 228.776 209.839 174.096 200.502C119.416 191.165 67.4148 228.541 57.9478 283.983C48.4809 339.426 85.1333 391.939 139.813 401.276C194.493 410.613 246.495 373.237 255.962 317.795Z"
        fill="#004742"
      />

      {/* Body front gradient circle */}
      <path
        d="M257.592 308.252C267.059 252.81 230.407 200.296 175.727 190.96C121.047 181.623 69.0454 218.999 59.5784 274.441C50.1115 329.883 86.764 382.397 141.444 391.734C196.124 401.071 248.126 363.695 257.592 308.252Z"
        fill={`url(#${prefix}_bodyFront)`}
      />

      {/* Inner face circle (color accent) */}
      <path
        d="M161.59 350.77C157.79 350.77 153.95 350.44 150.17 349.8C132.07 346.71 116.29 336.62 105.73 321.37C95.1798 306.14 91.0998 287.6 94.2398 269.15C100.03 235.25 128.66 210.65 162.34 210.65C166.14 210.65 169.98 210.98 173.76 211.62C191.86 214.71 207.64 224.8 218.2 240.05C228.75 255.28 232.83 273.82 229.69 292.27C223.9 326.17 195.27 350.77 161.59 350.77Z"
        fill="#7080F2"
      />

      {/* Inner face dark ring */}
      <path
        d="M232.671 288.487C239.395 249.104 213.363 211.803 174.525 205.171C135.687 198.539 98.7511 225.089 92.0264 264.471C85.3018 303.853 111.335 341.155 150.173 347.787C189.01 354.419 225.946 327.869 232.671 288.487Z"
        fill="black"
      />

      {/* Inner face shadow ring (opacity 0.57) */}
      <path
        opacity="0.57"
        d="M233.329 284.577C240.054 245.194 214.021 207.893 175.183 201.261C136.346 194.629 99.4098 221.179 92.6851 260.561C85.9604 299.943 111.993 337.245 150.831 343.877C189.669 350.508 226.605 323.959 233.329 284.577Z"
        fill="#004742"
      />

      {/* Neck/torso section highlight */}
      <path
        opacity="0.45"
        d="M97.01 198.14L113.85 291.22C117.25 309.99 131.73 324.81 150.42 328.64C171.45 332.94 192.7 322.35 201.93 302.97L241.11 220.63C254.24 193.04 237.76 160.49 207.76 154.74L151.73 143.99C119.54 137.81 91.17 165.88 97.01 198.14Z"
        fill={`url(#${prefix}_neckHighlight)`}
      />

      {/* Neck/torso section main */}
      <path
        d="M97.8498 193.19L114.69 286.27C118.09 305.04 132.57 319.86 151.26 323.69C172.29 327.99 193.54 317.4 202.77 298.02L241.95 215.68C255.08 188.09 238.6 155.54 208.6 149.79L152.57 139.04C120.38 132.86 92.0098 160.93 97.8498 193.19Z"
        fill={`url(#${prefix}_neck)`}
      />

      {/* Face feature - small accent left */}
      <path
        d="M190.99 264.82C190.31 265.04 189.6 265.19 188.86 265.24L190.99 264.82Z"
        fill="#0A0366"
      />

      {/* Face feature - right eye socket dark */}
      <path
        d="M198.65 243.76L197.22 257.16C196.83 260.82 194.29 263.74 191 264.83L188.87 265.26L178.97 267.23L179.25 264.38L174.97 263.92C170 263.39 167.47 258.08 167.47 253.08C167.47 249.85 165.54 241.06 165.54 241.06L198.67 243.77L198.65 243.76Z"
        fill="#0A0366"
      />

      {/* Face feature - left eye socket dark */}
      <path
        d="M164.84 237.09L163.41 250.49C162.95 254.75 159.57 258.03 155.48 258.54L155.51 258.56L145.21 260.56L144.48 257.47C140.37 256.2 138.14 251.33 137.58 246.84C136.99 242.03 131.08 229.54 131.08 229.54L164.83 237.1L164.84 237.09Z"
        fill="#0A0366"
      />

      {/* Left eye white */}
      <path
        d="M142.84 260.6L135.27 259.79C130.3 259.26 127.64 253.84 127.03 248.88C126.44 244.07 120.53 231.58 120.53 231.58L154.29 239.14L152.85 252.54C152.32 257.51 147.82 261.14 142.85 260.61L142.84 260.6Z"
        fill="white"
      />

      {/* Right eye white */}
      <path
        d="M176.65 267.27L164.39 265.96C159.42 265.43 156.89 260.12 156.89 255.12C156.89 251.89 154.96 243.1 154.96 243.1L188.09 245.81L186.65 259.21C186.12 264.18 181.62 267.81 176.65 267.28V267.27Z"
        fill="white"
      />

      {/* Silver head gradient */}
      <path
        d="M298.42 166.18L297.7 171.02C289.72 224.47 239.46 261.67 186.01 253.68L139.29 246.7C90.9899 239.49 57.3799 194.07 64.5899 145.78L66.7099 131.58C76.1199 68.56 135.39 24.69 198.41 34.1L200.94 34.48C263.97 43.89 307.83 103.16 298.42 166.18Z"
        fill={`url(#${prefix}_silverHead)`}
      />

      {/* Nose/brow left dark accent */}
      <path d="M147.09 226.52L143.94 224.55L146.1 221.67L147.09 226.52Z" fill="#0A0366" />

      {/* Nose/brow right dark accent */}
      <path
        d="M158.57 209.99L155.97 208.24L153.06 209.99L158.24 213.02L158.57 209.99Z"
        fill="#0A0366"
      />

      {/* Left eye ring dark outline */}
      <path
        d="M218.39 209.39C217.52 209.63 216.63 209.84 215.73 210.02C195.14 214.01 174.84 198.69 170.4 175.79C165.96 152.89 179.06 131.09 199.65 127.09C200.55 126.92 201.45 126.78 202.35 126.68C183.3 131.96 171.51 152.85 175.76 174.75C180.01 196.65 198.76 211.62 218.4 209.39H218.39Z"
        fill="#0A0366"
      />

      {/* Left eye ring gradient */}
      <path
        d="M218.407 209.423C237.708 205.679 249.761 184.117 245.327 161.264C240.893 138.412 221.652 122.922 202.35 126.667C183.049 130.411 170.996 151.973 175.43 174.826C179.864 197.678 199.105 213.168 218.407 209.423Z"
        fill={`url(#${prefix}_eyeRingLeft)`}
      />

      {/* Right eye ring dark outline */}
      <path
        d="M127.27 198.49C127.57 198.51 127.87 198.52 128.17 198.51C125.46 199.6 122.61 200.11 119.7 199.95C104.51 199.15 93.43 180.51 94.95 158.3C96.44 136.54 109.48 119.44 124.3 119.53C112.83 124.1 103.74 138.88 102.51 156.83C100.99 179.03 112.07 197.68 127.26 198.48L127.27 198.49Z"
        fill="#0A0366"
      />

      {/* Right eye ring gradient */}
      <path
        d="M151.598 161.035C152.931 138.978 142.129 120.379 127.471 119.493C112.812 118.606 99.8484 135.768 98.5148 157.825C97.1812 179.882 107.983 198.481 122.642 199.367C137.3 200.253 150.264 183.091 151.598 161.035Z"
        fill={`url(#${prefix}_eyeRingRight)`}
      />

      {/* Brow/nose bridge accent gradient */}
      <path
        d="M153.94 227.58L147.67 226.65C144.75 226.22 143.44 222.75 145.34 220.49L149.43 215.65L153.37 210.68C155.21 208.37 158.87 208.97 159.87 211.74L162.02 217.7L164.35 223.59C165.44 226.34 163.09 229.21 160.18 228.69L153.94 227.57V227.58Z"
        fill={`url(#${prefix}_noseBridge)`}
      />

      {/* Nose shadow detail */}
      <path
        d="M157 209.32C155.71 209.08 154.3 209.51 153.37 210.68L149.43 215.65L145.34 220.49C144.2 221.84 144.21 223.62 145.03 224.92C142.19 224.42 140.94 221.01 142.82 218.79L146.91 213.95L150.85 208.98C151.73 207.86 153.05 207.43 154.28 207.58C155.36 207.72 156.38 208.31 157 209.32Z"
        fill="#0A0366"
      />

      <defs>
        {/* Outer shadow gradient (neutral) */}
        <linearGradient
          id={`${prefix}_shadow`}
          x1="199.69"
          y1="59.251"
          x2="139.372"
          y2="412.498"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.54" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Body back gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_bodyBack`}
          x1="46.5211"
          y1="301.378"
          x2="263.333"
          y2="338.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>

        {/* Body front gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_bodyFront`}
          x1="175.114"
          y1="194.548"
          x2="141.303"
          y2="392.562"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>

        {/* Neck/torso highlight gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_neckHighlight`}
          x1="96.24"
          y1="236.36"
          x2="245.67"
          y2="236.36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>

        {/* Neck/torso main gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_neck`}
          x1="89.2474"
          y1="218.26"
          x2="236.65"
          y2="248.437"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>

        {/* Silver head gradient (neutral) */}
        <linearGradient
          id={`${prefix}_silverHead`}
          x1="63.6099"
          y1="143.79"
          x2="333.85"
          y2="143.79"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EDEEF0" />
          <stop offset="0.64" stopColor="#E4E5E7" />
          <stop offset="0.99" stopColor="#C7C8CA" />
        </linearGradient>

        {/* Left eye ring gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_eyeRingLeft`}
          x1="175.428"
          y1="174.816"
          x2="245.335"
          y2="161.253"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>

        {/* Right eye ring gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_eyeRingRight`}
          x1="97.9564"
          y1="164.727"
          x2="152.185"
          y2="154.205"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>

        {/* Nose bridge accent gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_noseBridge`}
          x1="144.178"
          y1="221.846"
          x2="163.361"
          y2="218.124"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyPrimary} />
          <stop offset="1" stopColor={colorScheme.bodySecondary} />
        </linearGradient>
      </defs>
    </svg>
  );
});

CryptPawnComponent.displayName = 'CryptPawnComponent';

export default CryptPawnComponent;
